import type { AxiosRequestConfig, AxiosResponse } from "axios";
import { axiosInstance } from "../axios-instance";
import type { ApiResponse, PaginatedResponse } from "../types";

/**
 * Get the appropriate API base URL based on environment
 * Always use relative paths to let proxy handle the routing
 * - Development: Vite dev server proxy
 * - Staging/Production: Nginx proxy
 */
function getApiBaseUrl(service: string): string {
  // Always use relative paths for all environments
  // This ensures requests go through the appropriate proxy:
  // - Vite proxy in development
  // - Nginx proxy in staging/production
  return service;
}

/**
 * Unified API client with consistent error handling and response formatting
 * This client provides a standardized way to make API calls across the application
 */
export class ApiClient {
  private baseURL: string;

  constructor(baseURL = "") {
    this.baseURL = baseURL;
  }

  /**
   * Build full URL with base URL
   */
  private buildUrl(endpoint: string): string {
    return this.baseURL ? `${this.baseURL}${endpoint}` : endpoint;
  }

  /**
   * Handle API response and extract data
   */
  private handleResponse<T>(response: AxiosResponse<ApiResponse<T>>): T {
    if (response.data && typeof response.data === "object" && "data" in response.data) {
      return response.data.data;
    }
    return response.data as T;
  }

  /**
   * Handle API errors consistently
   */
  private handleError(error: unknown): never {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unknown error occurred");
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await axiosInstance.get<ApiResponse<T>>(this.buildUrl(endpoint), config);
      return this.handleResponse(response);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * POST request
   */
  async post<T, D = unknown>(endpoint: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await axiosInstance.post<ApiResponse<T>>(
        this.buildUrl(endpoint),
        data,
        config,
      );
      return this.handleResponse(response);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * PUT request
   */
  async put<T, D = unknown>(endpoint: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await axiosInstance.put<ApiResponse<T>>(
        this.buildUrl(endpoint),
        data,
        config,
      );
      return this.handleResponse(response);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * PATCH request
   */
  async patch<T, D = unknown>(endpoint: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await axiosInstance.patch<ApiResponse<T>>(
        this.buildUrl(endpoint),
        data,
        config,
      );
      return this.handleResponse(response);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await axiosInstance.delete<ApiResponse<T>>(this.buildUrl(endpoint), config);
      return this.handleResponse(response);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * GET request with pagination support
   */
  async getPaginated<T>(
    endpoint: string,
    params?: Record<string, unknown>,
    config?: AxiosRequestConfig,
  ): Promise<PaginatedResponse<T>> {
    try {
      const response = await axiosInstance.get<ApiResponse<PaginatedResponse<T>>>(
        this.buildUrl(endpoint),
        { ...config, params },
      );
      return this.handleResponse(response);
    } catch (error) {
      this.handleError(error);
    }
  }
}

// Create default API client instance
export const apiClient = new ApiClient();

// Create specialized API clients for different services
// URLs are determined based on environment (relative for dev/test, absolute for prod/staging)
export const patientApiClient = new ApiClient(getApiBaseUrl("/api/patient"));
export const outpatientApiClient = new ApiClient(getApiBaseUrl("/api/outpatient"));
export const procedureApiClient = new ApiClient(getApiBaseUrl("/api/procedure"));
export const clinicApiClient = new ApiClient(getApiBaseUrl("/api/clinic"));
export const organisationApiClient = new ApiClient(getApiBaseUrl("/api/organisations"));
export const billingApiClient = new ApiClient(getApiBaseUrl("/api/billing"));
